import styled from '@emotion/styled';
import {observer} from 'mobx-react-lite';
import React from 'react';
import {ELabel} from 'webapp-lib/Components';
import {mixinFlexboxColumn} from 'webapp-lib/styling';

import {
  combineProps,
  convertPropsForField,
  FormFieldOmitProps,
  FormFieldOverrides,
  useController,
  UseControllerProps,
} from '_/utilities/form/controlled-components';
import {State} from '_/utilities/types';

import Loading from '../Loading';
import {SimplePlaceholder} from '../Placeholder/SimplePlaceholder';

import {Folder, FolderData} from './Folder';
import {FolderTree} from './FolderTree';

export type FolderTreeSelectProps = {
  className?: string;
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  loadSubFolders: (folderId: string) => Promise<FolderData[]>;
  rootFolder?: Folder;
  loadingStatus: State;
  missingFolderText?: string;
};

const UUncontrolledFolderTreeSelect = ({
  className = '',
  label,
  name,
  value,
  onChange,
  loadSubFolders,
  rootFolder,
  loadingStatus,
  missingFolderText,
}: FolderTreeSelectProps): React.ReactElement => {
  const handleChange = (folder: Folder) => {
    const clonedEvent = new Event('input');
    Object.defineProperty(clonedEvent, 'target', {
      writable: true,
      value: {value: folder.id, name},
    });
    onChange(clonedEvent as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <FlexColumnWrapper className={`FolderTreeSelect ${className}`}>
      <ELabel>{label}</ELabel>
      {loadingStatus === State.Loading ? (
        <Loading noDelay />
      ) : loadingStatus === State.Success && rootFolder ? (
        <FolderTree
          loadSubFolders={loadSubFolders}
          rootFolder={rootFolder}
          selectedFolderId={value}
          testId={name}
          onSelectedFolderChanged={handleChange}
        />
      ) : missingFolderText ? (
        <SimplePlaceholder customText={missingFolderText} />
      ) : null}
    </FlexColumnWrapper>
  );
};
const UncontrolledFolderTreeSelect = observer(UUncontrolledFolderTreeSelect);

export const FolderTreeSelect = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  customValue,
  customOnChange,
  ...elementProps
}: UseControllerProps<any> &
  Omit<FolderTreeSelectProps, FormFieldOmitProps> &
  FormFieldOverrides): React.ReactElement => {
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  const combinedProps = combineProps(formPropsForField, elementProps, {
    customValue,
    customOnChange,
  });
  return <UncontrolledFolderTreeSelect {...combinedProps} />;
};

const FlexColumnWrapper = styled.div`
  ${mixinFlexboxColumn}
  flex-wrap: wrap;
`;
